import { validateActionFlowTopologyContract } from "./actionflow-contract-validators";

export const ACTIONFLOW_TOPOLOGY_SIMULATION_PROOF_SCHEMA_VERSION =
  "actionflow-topology-simulation-proof.v1";

type JsonRecord = Record<string, unknown>;

export interface ActionFlowTopologySimulationState {
  local: JsonRecord;
  appData: JsonRecord;
  computed: JsonRecord;
  _results: JsonRecord;
  env: JsonRecord;
  route: JsonRecord;
  props: JsonRecord;
  event: JsonRecord;
  datasource: JsonRecord;
}

export interface SimulateActionFlowTopologyOptions {
  source?: string;
  generatedAt?: string;
  initialState?: Partial<ActionFlowTopologySimulationState>;
  event?: JsonRecord;
  datasourceMocks?: Record<string, unknown>;
}

export interface ActionFlowTopologySimulationStateChange {
  path: string;
  before: unknown;
  after: unknown;
}

export interface ActionFlowTopologySimulationStep {
  index: number;
  primitiveRef: string;
  primitiveKind: string;
  label: string;
  status: "ok" | "blocked" | "skipped" | "error";
  boundaryKinds: string[];
  evidenceRefs: string[];
  resolvedBindings: Record<string, unknown>;
  stateChanges: ActionFlowTopologySimulationStateChange[];
  resultKey?: string;
  message?: string;
}

export interface ActionFlowTopologySimulationProof {
  schemaVersion: typeof ACTIONFLOW_TOPOLOGY_SIMULATION_PROOF_SCHEMA_VERSION;
  source: string;
  generatedAt: string;
  ok: boolean;
  validationErrors: string[];
  errors: string[];
  initialState: ActionFlowTopologySimulationState;
  finalState: ActionFlowTopologySimulationState;
  steps: ActionFlowTopologySimulationStep[];
  summary: {
    primitivesSimulated: number;
    stateWrites: number;
    resultWrites: number;
    sideEffectMocks: number;
    blockedPrimitives: number;
    skippedPrimitives: number;
  };
}

interface SimulationContext {
  nodes: ReadonlyMap<string, JsonRecord>;
  effectBoundaries: ReadonlyMap<string, JsonRecord>;
  datasourceMocks: Record<string, unknown>;
  state: ActionFlowTopologySimulationState;
  guardBlockedBy?: string;
}

export function simulateActionFlowTopology(
  topologyValue: unknown,
  options: SimulateActionFlowTopologyOptions = {},
): ActionFlowTopologySimulationProof {
  const initialState = normalizeState(options.initialState, options.event);
  const state = cloneValue(initialState);
  const source = options.source ?? "headless-actionflow-topology-simulation";
  const generatedAt = options.generatedAt ?? new Date().toISOString();
  const validation = validateActionFlowTopologyContract(topologyValue);
  const validationErrors = validation.errors;
  const errors: string[] = [];
  const steps: ActionFlowTopologySimulationStep[] = [];

  if (!validation.ok) {
    return buildProof({
      source,
      generatedAt,
      validationErrors,
      errors,
      initialState,
      state,
      steps,
    });
  }

  const topology = asRecord(topologyValue);
  if (!topology) {
    return buildProof({
      source,
      generatedAt,
      validationErrors: ["actionFlowTopology must be an object"],
      errors,
      initialState,
      state,
      steps,
    });
  }

  const ctx: SimulationContext = {
    nodes: mapById(arrayRecords(topology.nodes)),
    effectBoundaries: mapById(arrayRecords(topology.effectBoundaries)),
    datasourceMocks: options.datasourceMocks ?? {},
    state,
  };

  for (const [index, primitive] of arrayRecords(topology.primitives).entries()) {
    const step = simulatePrimitive(primitive, index, ctx);
    steps.push(step);
    if (step.status === "error") {
      errors.push(
        `${step.primitiveRef}: ${step.message ?? "simulation failed"}`,
      );
    }
  }

  return buildProof({
    source,
    generatedAt,
    validationErrors,
    errors,
    initialState,
    state,
    steps,
  });
}

function simulatePrimitive(
  primitive: JsonRecord,
  index: number,
  ctx: SimulationContext,
): ActionFlowTopologySimulationStep {
  const primitiveRef = stringValue(primitive.id) ?? `primitive:${index}`;
  const primitiveKind = stringValue(primitive.kind) ?? "unknown";
  const bindings = arrayRecords(primitive.compilerParamBindings);
  const boundaryKinds = boundaryKindsForPrimitive(primitive, ctx);
  const step: ActionFlowTopologySimulationStep = {
    index,
    primitiveRef,
    primitiveKind,
    label: stringValue(primitive.label) ?? primitiveRef,
    status: "ok",
    boundaryKinds,
    evidenceRefs: stringArray(primitive.evidenceRefs),
    resolvedBindings: {},
    stateChanges: [],
  };

  try {
    if (ctx.guardBlockedBy && sideEffectBoundaryKinds(boundaryKinds).length > 0) {
      step.status = "skipped";
      step.message = `Skipped because ${ctx.guardBlockedBy} blocked side effects.`;
      return step;
    }

    for (const binding of bindings) {
      if (hasResolvableValue(binding)) {
        const bindingKey = bindingKeyFor(binding);
        step.resolvedBindings[bindingKey] = resolveBindingValue(binding, ctx);
      }
    }

    switch (primitiveKind) {
      case "set-local-state":
      case "promote-local-to-global":
      case "hydrate-local-from-global":
      case "compute-projection":
        writeTargetNode(
          roleBinding(bindings, "target"),
          roleBinding(bindings, "value"),
          ctx,
          step,
        );
        break;
      case "fetch-datasource-to-global":
        simulateDatasourceFetch(primitive, bindings, ctx, step);
        break;
      case "write-global-to-datasource":
        simulateDatasourceWrite(primitive, bindings, ctx, step);
        break;
      case "navigate":
        simulateNavigation(bindings, ctx, step);
        break;
      case "guard-by-permission":
        simulateGuard(bindings, ctx, step);
        break;
      case "trigger-ui-feedback":
        simulateUiFeedback(bindings, ctx, step);
        break;
      case "subscribe-realtime-source":
        simulateRealtimeSubscription(primitive, bindings, ctx, step);
        break;
      default:
        throw new Error(`Unsupported primitive kind ${primitiveKind}`);
    }
  } catch (error) {
    step.status = "error";
    step.message = error instanceof Error ? error.message : String(error);
  }

  return step;
}

function writeTargetNode(
  targetBinding: JsonRecord | undefined,
  valueBinding: JsonRecord | undefined,
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const targetNode = nodeFromBinding(targetBinding, "target", ctx);
  const value = valueBinding
    ? resolveBindingValue(valueBinding, ctx)
    : readStatePath(ctx.state, nodePath(targetNode));
  writeNodePath(ctx.state, nodePath(targetNode), value, step);
}

function simulateDatasourceFetch(
  primitive: JsonRecord,
  bindings: JsonRecord[],
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const sourceNode = nodeFromBinding(roleBinding(bindings, "source"), "source", ctx);
  const targetNode = nodeFromBinding(roleBinding(bindings, "target"), "target", ctx);
  const datasourceName = datasourceNameFor(sourceNode);
  const mockValue =
    datasourceName in ctx.datasourceMocks
      ? ctx.datasourceMocks[datasourceName]
      : {
          mocked: true,
          primitiveRef: stringValue(primitive.id),
          datasourceName,
        };
  writeNodePath(ctx.state, nodePath(targetNode), mockValue, step);
  writeResultKey(bindings, mockValue, ctx, step);
}

function simulateDatasourceWrite(
  primitive: JsonRecord,
  bindings: JsonRecord[],
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const sourceNode = nodeFromBinding(roleBinding(bindings, "source"), "source", ctx);
  const payloadBinding = roleBinding(bindings, "payload");
  const outputNodeId = stringArray(primitive.outputNodeIds)[0];
  const datasourceNode = outputNodeId ? ctx.nodes.get(outputNodeId) : undefined;
  const datasourceName = datasourceNode
    ? datasourceNameFor(datasourceNode)
    : "unknownDatasource";
  const payload = payloadBinding
    ? resolveBindingValue(payloadBinding, ctx)
    : readStatePath(ctx.state, nodePath(sourceNode));
  const result =
    datasourceName in ctx.datasourceMocks
      ? ctx.datasourceMocks[datasourceName]
      : {
          ok: true,
          primitiveRef: stringValue(primitive.id),
          datasourceName,
          payload,
        };
  writeStatePath(
    ctx.state,
    `datasource.${datasourceName}.lastWrite`,
    payload,
    step,
  );
  writeResultKey(bindings, result, ctx, step);
}

function simulateNavigation(
  bindings: JsonRecord[],
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const routeBinding = roleBinding(bindings, "route");
  if (!routeBinding) return;
  const routeValue = resolveBindingValue(routeBinding, ctx);
  if (typeof routeValue === "string") {
    writeStatePath(ctx.state, "route.path", routeValue, step);
  }
}

function simulateGuard(
  bindings: JsonRecord[],
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const conditionBinding = roleBinding(bindings, "condition");
  const allowed = conditionBinding
    ? Boolean(resolveBindingValue(conditionBinding, ctx))
    : false;
  step.resolvedBindings.guardResult = allowed;
  if (!allowed) {
    step.status = "blocked";
    ctx.guardBlockedBy = step.primitiveRef;
    step.message = "Permission guard evaluated to false.";
  }
}

function simulateUiFeedback(
  bindings: JsonRecord[],
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const payloadBinding = roleBinding(bindings, "payload");
  const sourceBinding = roleBinding(bindings, "source");
  const payload = payloadBinding
    ? resolveBindingValue(payloadBinding, ctx)
    : sourceBinding
      ? resolveBindingValue(sourceBinding, ctx)
      : "Action completed";
  step.resolvedBindings.uiFeedback = {
    mocked: true,
    payload,
  };
}

function simulateRealtimeSubscription(
  primitive: JsonRecord,
  bindings: JsonRecord[],
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const sourceNode = nodeFromBinding(roleBinding(bindings, "source"), "source", ctx);
  const targetNode = nodeFromBinding(roleBinding(bindings, "target"), "target", ctx);
  const datasourceName = datasourceNameFor(sourceNode);
  const mockValue =
    datasourceName in ctx.datasourceMocks
      ? ctx.datasourceMocks[datasourceName]
      : {
          mocked: true,
          primitiveRef: stringValue(primitive.id),
          datasourceName,
          realtime: true,
          rows: [],
        };
  writeNodePath(ctx.state, nodePath(targetNode), mockValue, step);
  writeResultKey(bindings, mockValue, ctx, step);
}

function writeResultKey(
  bindings: JsonRecord[],
  value: unknown,
  ctx: SimulationContext,
  step: ActionFlowTopologySimulationStep,
): void {
  const resultKey = stringValue(roleBinding(bindings, "result")?.resultKey);
  if (!resultKey) return;
  step.resultKey = resultKey;
  writeStatePath(ctx.state, `_results.${resultKey}`, value, step);
}

function resolveBindingValue(binding: JsonRecord, ctx: SimulationContext): unknown {
  const expression = stringValue(binding.expression);
  if (expression) return resolveValue(expression, ctx.state);
  const rawValue = binding.value;
  if (typeof rawValue === "string" && rawValue.includes("#{")) {
    return resolveValue(rawValue, ctx.state);
  }
  if ("value" in binding) return cloneValue(rawValue);
  const sourceNodeId = stringValue(binding.sourceNodeId);
  if (sourceNodeId) {
    const sourceNode = ctx.nodes.get(sourceNodeId);
    if (!sourceNode) throw new Error(`Missing source node ${sourceNodeId}`);
    if (sourceNode.scope === "datasource") return datasourceNameFor(sourceNode);
    return readStatePath(ctx.state, nodePath(sourceNode));
  }
  const targetNodeId = stringValue(binding.targetNodeId);
  if (targetNodeId) {
    const targetNode = ctx.nodes.get(targetNodeId);
    if (!targetNode) throw new Error(`Missing target node ${targetNodeId}`);
    return nodePath(targetNode);
  }
  return undefined;
}

function resolveValue(
  rawValue: string,
  state: ActionFlowTopologySimulationState,
): unknown {
  const match = rawValue.match(/^#\{([\s\S]+)\}$/);
  if (!match) return rawValue;
  return evaluateExpression(match[1]!.trim(), state);
}

function evaluateExpression(
  expression: string,
  state: ActionFlowTopologySimulationState,
): unknown {
  const concatArgs = functionArgs(expression, "concat");
  if (concatArgs) {
    const values = splitArguments(concatArgs).map((arg) =>
      evaluateTerm(arg, state),
    );
    const first = values[0];
    if (Array.isArray(first)) return first.concat(...values.slice(1));
    return values.map((value) => stringifyExpressionValue(value)).join("");
  }

  const countArgs = functionArgs(expression, "$count");
  if (countArgs) {
    const value = evaluateTerm(countArgs, state);
    if (Array.isArray(value) || typeof value === "string") return value.length;
    if (value && typeof value === "object") return Object.keys(value).length;
    return 0;
  }

  const mathRoundArgs = functionArgs(expression, "Math.round")
    ?? functionArgs(expression, "round");
  if (mathRoundArgs) return Math.round(evaluateNumericExpression(mathRoundArgs, state));

  const reduceMatch = expression.match(/^(.+)\.reduce\(\(sum,\s*item\)\s*=>\s*sum\s*\+\s*item\.([A-Za-z0-9_]+)(?:\s*\*\s*item\.([A-Za-z0-9_]+))?,\s*0\)$/);
  if (reduceMatch) {
    const collection = evaluateTerm(reduceMatch[1]!, state);
    if (!Array.isArray(collection)) {
      throw new Error(`Expression ${expression} expected an array`);
    }
    const leftKey = reduceMatch[2]!;
    const rightKey = reduceMatch[3];
    return collection.reduce((sum, item) => {
      const record = asRecord(item);
      const left = toNumber(record?.[leftKey], `${leftKey} in ${expression}`);
      const value = rightKey
        ? left * toNumber(record?.[rightKey], `${rightKey} in ${expression}`)
        : left;
      return sum + value;
    }, 0);
  }

  const trimIncludesMatch = expression.match(/^(.+)\.trim\(\)\.includes\((.+)\)$/);
  if (trimIncludesMatch) {
    const value = evaluateTerm(trimIncludesMatch[1]!, state);
    const needle = evaluateTerm(trimIncludesMatch[2]!, state);
    return String(value).trim().includes(String(needle));
  }

  const includesMatch = expression.match(/^(.+)\.includes\((.+)\)$/);
  if (includesMatch) {
    const value = evaluateTerm(includesMatch[1]!, state);
    const needle = evaluateTerm(includesMatch[2]!, state);
    return String(value).includes(String(needle));
  }

  if (expression.includes("*")) {
    return evaluateNumericExpression(expression, state);
  }

  return evaluateTerm(expression, state);
}

function evaluateTerm(
  rawTerm: string,
  state: ActionFlowTopologySimulationState,
): unknown {
  const term = rawTerm.trim();
  if (
    (term.startsWith("'") && term.endsWith("'")) ||
    (term.startsWith('"') && term.endsWith('"'))
  ) {
    return term.slice(1, -1);
  }
  if (term === "true") return true;
  if (term === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(term)) return Number(term);
  if (/^(local|appData|computed|_results|env|route|props|event|datasource)(\.|$)/.test(term)) {
    return readStatePath(state, term);
  }
  throw new Error(`Unsupported expression term ${term}`);
}

function evaluateNumericExpression(
  expression: string,
  state: ActionFlowTopologySimulationState,
): number {
  const factors = expression.split("*").map((part) => part.trim());
  if (factors.length > 1) {
    return factors.reduce(
      (product, factor) =>
        product * toNumber(evaluateTerm(factor, state), factor),
      1,
    );
  }
  return toNumber(evaluateTerm(expression, state), expression);
}

function functionArgs(expression: string, fnName: string): string | null {
  const prefix = `${fnName}(`;
  if (!expression.startsWith(prefix) || !expression.endsWith(")")) return null;
  return expression.slice(prefix.length, -1);
}

function splitArguments(input: string): string[] {
  const args: string[] = [];
  let current = "";
  let depth = 0;
  let quote: "'" | '"' | null = null;
  for (const char of input) {
    if (quote) {
      current += char;
      if (char === quote) quote = null;
      continue;
    }
    if (char === "'" || char === '"') {
      quote = char;
      current += char;
      continue;
    }
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (char === "," && depth === 0) {
      args.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  if (current.trim()) args.push(current.trim());
  return args;
}

function nodeFromBinding(
  binding: JsonRecord | undefined,
  role: string,
  ctx: SimulationContext,
): JsonRecord {
  const nodeId = stringValue(
    role === "target" ? binding?.targetNodeId : binding?.sourceNodeId,
  );
  if (!nodeId) throw new Error(`Missing ${role} node binding`);
  const node = ctx.nodes.get(nodeId);
  if (!node) throw new Error(`Missing ${role} node ${nodeId}`);
  return node;
}

function writeNodePath(
  state: ActionFlowTopologySimulationState,
  path: string,
  value: unknown,
  step: ActionFlowTopologySimulationStep,
): void {
  writeStatePath(state, path, value, step);
}

function readStatePath(
  state: ActionFlowTopologySimulationState,
  path: string,
): unknown {
  const [scope, ...rest] = path.split(".");
  if (!scope || !(scope in state)) {
    throw new Error(`Expression path ${path} has unknown scope`);
  }
  let current: unknown = state[scope as keyof ActionFlowTopologySimulationState];
  for (const segment of rest) {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      throw new Error(`Expression path ${path} is not available`);
    }
    current = (current as JsonRecord)[segment];
  }
  if (current === undefined) {
    throw new Error(`Expression path ${path} is not available`);
  }
  return current;
}

function writeStatePath(
  state: ActionFlowTopologySimulationState,
  path: string,
  value: unknown,
  step: ActionFlowTopologySimulationStep,
): void {
  const [scope, ...rest] = path.split(".");
  if (!scope || !(scope in state)) {
    throw new Error(`State path ${path} has unknown scope`);
  }
  if (rest.length === 0) {
    throw new Error(`State path ${path} must include a nested field`);
  }
  let current = state[scope as keyof ActionFlowTopologySimulationState];
  for (const segment of rest.slice(0, -1)) {
    const next = current[segment];
    if (!next || typeof next !== "object" || Array.isArray(next)) {
      current[segment] = {};
    }
    current = current[segment] as JsonRecord;
  }
  const leaf = rest[rest.length - 1]!;
  const before = cloneValue(current[leaf]);
  current[leaf] = cloneValue(value);
  step.stateChanges.push({
    path,
    before,
    after: cloneValue(value),
  });
}

function normalizeState(
  initialState: Partial<ActionFlowTopologySimulationState> | undefined,
  event: JsonRecord | undefined,
): ActionFlowTopologySimulationState {
  return {
    local: cloneRecord(initialState?.local),
    appData: cloneRecord(initialState?.appData),
    computed: cloneRecord(initialState?.computed),
    _results: cloneRecord(initialState?._results),
    env: cloneRecord(initialState?.env),
    route: cloneRecord(initialState?.route),
    props: cloneRecord(initialState?.props),
    event: cloneRecord(event ?? initialState?.event),
    datasource: cloneRecord(initialState?.datasource),
  };
}

function buildProof(options: {
  source: string;
  generatedAt: string;
  validationErrors: string[];
  errors: string[];
  initialState: ActionFlowTopologySimulationState;
  state: ActionFlowTopologySimulationState;
  steps: ActionFlowTopologySimulationStep[];
}): ActionFlowTopologySimulationProof {
  const resultWrites = options.steps.reduce(
    (count, step) => count + (step.resultKey ? 1 : 0),
    0,
  );
  return {
    schemaVersion: ACTIONFLOW_TOPOLOGY_SIMULATION_PROOF_SCHEMA_VERSION,
    source: options.source,
    generatedAt: options.generatedAt,
    ok: options.validationErrors.length === 0 && options.errors.length === 0,
    validationErrors: options.validationErrors,
    errors: options.errors,
    initialState: cloneValue(options.initialState),
    finalState: cloneValue(options.state),
    steps: options.steps.map((step) => cloneValue(step)),
    summary: {
      primitivesSimulated: options.steps.length,
      stateWrites: options.steps.reduce(
        (count, step) => count + step.stateChanges.length,
        0,
      ),
      resultWrites,
      sideEffectMocks: options.steps.filter(
        (step) => sideEffectBoundaryKinds(step.boundaryKinds).length > 0,
      ).length,
      blockedPrimitives: options.steps.filter(
        (step) => step.status === "blocked",
      ).length,
      skippedPrimitives: options.steps.filter(
        (step) => step.status === "skipped",
      ).length,
    },
  };
}

function hasResolvableValue(binding: JsonRecord): boolean {
  return (
    typeof binding.expression === "string" ||
    "value" in binding ||
    typeof binding.sourceNodeId === "string" ||
    typeof binding.targetNodeId === "string"
  );
}

function bindingKeyFor(binding: JsonRecord): string {
  const role = stringValue(binding.role) ?? "unknown";
  const param = stringValue(binding.param);
  return param ? `${role}:${param}` : role;
}

function roleBinding(bindings: JsonRecord[], role: string): JsonRecord | undefined {
  return bindings.find((binding) => binding.role === role);
}

function boundaryKindsForPrimitive(
  primitive: JsonRecord,
  ctx: SimulationContext,
): string[] {
  return stringArray(primitive.effectBoundaryRefs)
    .map((boundaryRef) => stringValue(ctx.effectBoundaries.get(boundaryRef)?.kind))
    .filter((kind): kind is string => Boolean(kind));
}

function sideEffectBoundaryKinds(boundaryKinds: readonly string[]): string[] {
  return boundaryKinds.filter(
    (kind) => kind !== "pure" && kind !== "state" && kind !== "auth",
  );
}

function datasourceNameFor(node: JsonRecord): string {
  return stringValue(node.datasourceName) ?? nodePath(node);
}

function nodePath(node: JsonRecord): string {
  const path = stringValue(node.path);
  if (!path) throw new Error("Topology node is missing path");
  return path;
}

function mapById(records: JsonRecord[]): Map<string, JsonRecord> {
  const map = new Map<string, JsonRecord>();
  for (const record of records) {
    const id = stringValue(record.id);
    if (id) map.set(id, record);
  }
  return map;
}

function arrayRecords(value: unknown): JsonRecord[] {
  return Array.isArray(value)
    ? value.filter((item): item is JsonRecord => Boolean(asRecord(item)))
    : [];
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asRecord(value: unknown): JsonRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : null;
}

function cloneRecord(value: unknown): JsonRecord {
  const record = asRecord(value);
  return record ? cloneValue(record) : {};
}

function cloneValue<T>(value: T): T {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value)) as T;
}

function stringifyExpressionValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function toNumber(value: unknown, label: string): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  throw new Error(`Expression value ${label} must be numeric`);
}
